import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import { randomUUID as uuidv4 } from 'expo-crypto';

import { Cognito } from 'utils/cognito';
import { withNetworkRetries } from 'utils/network/utils';

import normalizeString, { getCognitoRetryConfig } from './util';

function attributeObjectToAttributeList(attributes: ISignUpAttributes | IUpdateUserAttributes) {
  return Object.entries(attributes).flatMap(([Name, Value]) =>
    Value == null ? [] : [new CognitoUserAttribute({ Name, Value })]
  );
}

interface ISignUp {
  username: string;
  name: string;
  phoneNumber: string;
  wantsPromotionalEmails: boolean;
  country: string;
  dob?: string;
}

interface IValidateLogin {
  username: string;
  code: string;
  sessionId: string;
}

interface ISignUpAttributes {
  nickname: string;
  name: string;
  locale: string;
  phone_number?: string;
  birthdate?: string;
}

interface IUpdateUserAttributes {
  name?: string;
  phoneNumber?: string | null;
  dob?: string;
}

////////////////////////////////////////////////////////////
// Sign In Moved to GQL queries
////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////
// Sign Out
////////////////////////////////////////////////////////////

export const signOut = async (isGlobalSignOut: boolean = false): Promise<void> => {
  const { user } = await getUserAndSession();

  if (isGlobalSignOut) {
    return new Promise((resolve, reject) =>
      user.globalSignOut({ onSuccess: () => resolve(), onFailure: reject })
    );
  }

  return new Promise((resolve, _reject) => user.signOut(() => resolve()));
};

////////////////////////////////////////////////////////////
// Sign Up
////////////////////////////////////////////////////////////

export const signUp = ({
  username,
  wantsPromotionalEmails,
  name,
  phoneNumber,
  country,
  dob,
}: ISignUp): Promise<ISignUpResult> => {
  // TODO: customAttributes must be predefined using nickname as a hack
  const normalizedUsername = normalizeString(username);
  const password = uuidv4();
  const emailConsent = wantsPromotionalEmails ? 'true' : 'false';
  const attributes = attributeObjectToAttributeList({
    nickname: emailConsent,
    name,
    locale: country,
    phone_number: phoneNumber,
    birthdate: dob,
  });

  const userPool = Cognito.userPool();

  return withNetworkRetries(
    () =>
      new Promise((resolve, reject) =>
        userPool.signUp(normalizedUsername, password, attributes, [], (err, result) => {
          if (err || result == null) {
            return reject(err);
          }
          return resolve(result);
        })
      ),
    getCognitoRetryConfig('Auth.signUp')
  );
};

////////////////////////////////////////////////////////////
// Confirm Login
////////////////////////////////////////////////////////////

export const validateLogin = async ({
  username,
  code,
  sessionId,
}: IValidateLogin): Promise<CognitoUserSession | null> => {
  const cognitoUser = getCognitoUserForUsername(username);
  (cognitoUser as any).Session = sessionId;

  return withNetworkRetries(
    () =>
      new Promise<CognitoUserSession>((resolve, reject) =>
        cognitoUser.sendCustomChallengeAnswer(code, {
          onSuccess: session => {
            resolve(session);
          },
          onFailure: err => {
            reject(err);
          },
        })
      ),
    getCognitoRetryConfig('Auth.sendCustomChallengeAnswer')
  );
};

////////////////////////////////////////////////////////////
// Update User Attributes
////////////////////////////////////////////////////////////

export const updateUserAttributes = async ({ name, phoneNumber, dob }: IUpdateUserAttributes) => {
  const { user } = await getUserAndSession();

  const attributesToUpdate = attributeObjectToAttributeList({
    name,
    phone_number: phoneNumber || undefined,
    birthdate: dob,
  });

  return new Promise<string>((resolve, reject) =>
    user.updateAttributes(attributesToUpdate, (err, result) => {
      if (err || !result) {
        return reject(err);
      }
      return resolve(result);
    })
  );
};

////////////////////////////////////////////////////////////
// Helper
////////////////////////////////////////////////////////////

const getCognitoUserForUsername = (username: string): CognitoUser => {
  const normalizedUsername = normalizeString(username);
  return new CognitoUser({
    Username: normalizedUsername,
    Pool: Cognito.userPool(),
    Storage: Cognito.storage,
  });
};

// getSession (in addition to getCurrentUser) is often required before logout/update
const getUserAndSession = async () => {
  return new Promise<{ user: CognitoUser; session: CognitoUserSession }>((resolve, reject) => {
    const user = Cognito.userPool().getCurrentUser();
    if (!user) {
      reject(new Error('No current user'));
      return;
    }
    user.getSession((err, session) => {
      if (err || !session) {
        return reject(err || new Error('No session'));
      }
      resolve({ user, session });
    });
  });
};
